import React, { useEffect, useState } from "react";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";

import { useDialog } from "../../dialogContext";
import { SensorDialog, SensorHistoryDialog } from "./dialog";
import {
  useSensors,
  useCreateSensor,
  useEditSensor,
  useDeleteSensor,
} from "../../../api/queries/sensors";
import { Loading } from "../../loadingContext";
import { PlusCircleIcon } from "@heroicons/react/24/solid";
import { useUserToken } from "../../util";

export const Sensors = () => {
  useUserToken();

  const { unSetDialog, setDialogWithSize: setDialog } = useDialog();
  const sensors = useSensors({ activeOnly: false });
  const deleteSensor = useDeleteSensor();
  const createSensor = useCreateSensor();
  const editSensor = useEditSensor();
  const [processedSensors, setProcessedSensors] = useState([]);

  const onNewSensor = async (formData) => {
    // Remove SiteData since it's no longer needed for manual sitedownloadstatus management
    delete formData.SiteData;
    
    // Create the sensor - Firebase function will handle sitedownloadstatus automatically
    await createSensor.mutateAsync(formData);
    unSetDialog();
  };

  const onEditSensor = async (formData) => {
    // Remove SiteData since it's no longer needed for manual sitedownloadstatus management
    delete formData.SiteData;
    
    if (formData.id === '') {
      // This is a new sensor being created (when editing resulted in creating new sensor)
      await createSensor.mutateAsync(formData);
    } else {
      // This is updating an existing sensor
      editSensor.mutate(formData);
    }
    // Firebase function will handle sitedownloadstatus automatically
  };

  const onDeleteSensor = (targetID, historyIDs=[]) => {
    function deleteSensorStatus(targetID) {
      deleteSensor.mutate(targetID);
      // Remove manual sitedownloadstatus deletion - Firebase function will handle it

      historyIDs.forEach((entry) => {
        deleteSensor.mutate(entry);
      });
    }
    confirmAlert({
      title: "Delete",
      message: "Are you sure you want to delete this record?",
      buttons: [
        { label: "Yes", onClick: () => deleteSensorStatus(targetID) },
        {
          label: "No",
          onClick: () => {},
        },
      ],
      closeOnClickOutside: true,
      closeOnEscape: true,
      overlayClassName: "!z-[1000]",
    });
  };

  useEffect(() => {
    if (sensors.data) {
      const groupedSensors = sensors.data.reduce((acc, sensor) => {
        const deviceID = sensor.data().DeviceID;
        if (!acc[deviceID]) {
          acc[deviceID] = [];
        }
        acc[deviceID].push({ ...sensor.data(), id: sensor.id });
        return acc;
      }, {});

      const processed = Object.values(groupedSensors).map((group) => {
        group.sort((a, b) => b.StartDate.toMillis() - a.StartDate.toMillis());
        const latest = group[0];
        const history = group.slice(1);
        return { latest, history };
      });
      console.log(processed);
      setProcessedSensors(processed);
    }
  }, [sensors.data]);

  return (
    <div className="overflow-x-auto w-full h-full">
      {(createSensor.isPending ||
        editSensor.isPending ||
        deleteSensor.isPending) && <Loading />}
      <button
        className="m-2 fixed bottom-8 right-0 z-50"
        onClick={() =>
          setDialog(
            <SensorDialog onSubmit={onNewSensor} unSetDialog={unSetDialog} />
          )
        }
      >
        <PlusCircleIcon className="h-10 w-10 z-50" />
      </button>

      <table className="table table-zebra">
        <thead>
          <tr>
            <th>Device ID</th>
            <th>Site</th>
            <th>Start date</th>
            <th className="w-24"></th>
            <th className="w-24"></th>
          </tr>
        </thead>
        <tbody>
          {sensors.isLoading ? (
            <Loading />
          ) : sensors.isError ? (
            <></>
          ) : (
            processedSensors.map(({ latest, history }) => (
              <tr key={latest.id}>
                <td>{latest.DeviceID}</td>
                <td>{latest.SiteName}</td>
                {latest.StartDate ? (
                  <td>
                    {latest.StartDate
                      .toDate()
                      .toLocaleDateString("en-GB")}
                  </td>
                ) : (
                  <td></td>
                )}
                <td>
                  <button
                    className="btn btn-ghost btn-xs"
                    onClick={() =>
                      setDialog(
                        <SensorHistoryDialog
                          deviceID={latest.DeviceID}
                          onEditSensor={onEditSensor}
                          onDeleteSensor={onDeleteSensor}
                          onNewSensor={onNewSensor}
                          unSetDialog={unSetDialog}
                        />
                      , "large")
                    }
                  >
                    Edit
                  </button>
                </td>
                <td>
                  <button
                    className="btn btn-ghost btn-xs"
                    onClick={() => onDeleteSensor(latest.id, history.map(h => h.id))}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};
